import React from 'react';
import {
	useForm,
	type FormAction,
	type FormType,
	type FormOptions,
	type FormValues,
} from './hooks/useForm';
import type {InputForm} from './typings';
import {getInputFormObject} from './util';

export type WithFormProps<
	T extends object = {},
	TValues extends FormValues = FormValues,
	TAction extends FormAction = FormAction,
> = WithFormPropsIn<T, TValues, TAction> &
	WithFormPropsOut<T, TValues, TAction>;

export type WithFormPropsIn<
	T extends object = {},
	TValues extends FormValues = FormValues,
	TAction extends FormAction = FormAction,
> = T & {
	formOptions?: FormOptions<TValues, TAction>;
	refreshAt?: number;
	values?: TValues;
};

export type WithFormPropsOut<
	T extends object = {},
	TValues extends FormValues = FormValues,
	TAction extends FormAction = FormAction,
> = Omit<T, 'formOptions' | 'values'> & {
	form: FormType<TValues, TAction>;
	inputForm: InputForm<TValues, TAction>;
};

export interface WithFormOptions<
	TValues extends FormValues = {},
	TAction extends FormAction = FormAction,
> extends FormOptions<TValues, TAction> {}

/**
 * Wrap component with form support
 * @deprecated
 */
export const withForm = <
	P extends {},
	TValues extends FormValues = FormValues,
	TAction extends FormAction = FormAction,
	Options extends WithFormOptions<TValues> = {},
>(
	Component: React.ComponentType<P>,
	options?: Options,
): React.FunctionComponent<WithFormPropsIn & P> => {
	return ({formOptions, refreshAt, values, ...props}) => {
		const form = useForm<TValues, TAction>(
			values as TValues,
			{
				...options,
				...formOptions,
			} as FormOptions<TValues>,
		);

		const inputForm = getInputFormObject(form);

		return (
			<Component
				{...(props as P)}
				form={form}
				inputForm={inputForm}
				refreshAt={refreshAt}
			/>
		);
	};
};

export default withForm;
